'use client'

import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'

import { cn } from '@/lib/utils'

const TooltipProvider = TooltipPrimitive.Provider

const TooltipRoot = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipPortal = TooltipPrimitive.Portal

// Gold-accented tooltip that adapts to light/dark theme
// z-[80] to appear above drawers (z-[60]) and dialogs (z-[70])
const tooltipContentStyles =
  'z-[80] overflow-hidden rounded-md px-2.5 py-1.5 text-xs border shadow-lg animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 bg-[var(--dday-gold-50)] text-[var(--dday-gold-900)] border-primary shadow-[0_4px_12px_rgba(212,175,55,0.2)] dark:bg-[var(--dday-gold-900)] dark:text-[var(--dday-gold-100)] dark:border-[var(--dday-gold-600)] dark:shadow-[0_4px_12px_rgba(212,175,55,0.15)]'

interface TooltipContentProps extends React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
> {
  showArrow?: boolean
}

const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(
  (
    { className, showArrow = true, sideOffset = 6, children, ...props },
    ref
  ) => (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(tooltipContentStyles, className)}
      {...props}
    >
      {children}
      {showArrow && (
        <TooltipPrimitive.Arrow
          className="fill-[var(--dday-gold-50)] dark:fill-[var(--dday-gold-900)]"
          width={10}
          height={5}
        />
      )}
    </TooltipPrimitive.Content>
  )
)
TooltipContent.displayName = TooltipPrimitive.Content.displayName

// Simple wrapper component for common use cases
interface TooltipProps {
  content: React.ReactNode
  children: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  delayDuration?: number
  showArrow?: boolean
  asChild?: boolean
}

function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  delayDuration = 200,
  showArrow = true,
  asChild = true,
}: TooltipProps) {
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <TooltipRoot>
        <TooltipTrigger asChild={asChild}>{children}</TooltipTrigger>
        <TooltipPortal>
          <TooltipContent side={side} align={align} showArrow={showArrow}>
            {content}
          </TooltipContent>
        </TooltipPortal>
      </TooltipRoot>
    </TooltipProvider>
  )
}

export {
  Tooltip,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipPortal,
  TooltipContent,
}
